import React, { Component, Fragment } from 'react';
import moment from 'moment';
import { HelpIcon } from 'components/atoms/HelpIcon';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Popover, OverlayTrigger } from 'react-bootstrap';
import { Field, change } from 'redux-form';
import { get } from 'lodash';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import TextInputAdmin from 'components/atoms/inputs/TextInputAdmin';
import IconWrapper from 'components/atoms/IconWrapper';
import SearchOpportunities from 'components/molecules/modal/SearchOpportunities';
import WorkOrderChannelConfig from 'components/organisms/entity/edit/WorkOrderChannelConfig';
import TrackedComponentEnhancer from 'hoc/TrackedComponent';
import ListItemTile from 'components/atoms/layout/ListItemTile';

const ADMIN_PLATFORM_MAP = {
    ccms: 'Job Number',
    salesforce: 'Salesforce Opportunity ID',
};

const ADMIN_PLATFORM_TITLE_MAP = {
    ccms: 'CCMS',
    salesforce: 'Campaign Engine',
};

const ADMIN_PLATFORM_FIELD_MAP = {
    ccms: 'jobNumber',
    salesforce: 'salesforceId',
};

const LinkText = styled.a`
    cursor: pointer;
    font-size: 15px;
    display:block;
    top: -8px;
    position: relative;
    text-decoration: underline;
    margin-left: 1rem;
    i {
        font-style: normal;
    }
`;

const RequiredLabel = styled.label`
    &:before {
        content: ${props => props.required && ("'*'")};
        color: ${props => props.theme.colors.flamepea};
        margin-right: 3px;
    }
`;

const ErrorLabel = styled.p`
    color: ${props => props.theme.colors.flamepea};
`;

const FlexWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const ActionButton = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    cursor: pointer;
    line-height: 0;
    margin-bottom: 5px;
    font-size: 12px;

    :hover {
         span {
            color: #000;
         }
    }
`;

const ActionsWrapper = styled.div`
    position: absolute;
    right: 30px;   
    display: flex;
    flex-direction: column;
    min-height: 20px;
`;

const UpperWorkOrder = styled.div`
    padding: 10px;
`;

const TrackedActionButton = TrackedComponentEnhancer({
    trackedName: 'Clone-Work-Order',
    targetComponent: ActionButton,
    sendToMixPanel: true
});

class WorkOrder extends Component {
    constructor(props) {
        super(props);

        this.state = {
            fadingOut: false,
            mountSearchModal: false
        };

        this.handleDelete = this.handleDelete.bind(this);
        this.handleClone = this.handleClone.bind(this);
        this.createDatePickerProps = this.createDatePickerProps.bind(this);
    }

    getAdminPlatform() {
        return get(this.props.workOrder, 'adminPlatform', null);
    }

    getAdminPlatformFromMap(MAP) {
        const adminPlatform = this.getAdminPlatform();

        if (adminPlatform) {
            return MAP[adminPlatform];
        }

        // this should never hit, here for development
        return 'Invalid Platform';
    }

    createDatePickerProps(fieldName) {
        const resultProps = {
            name: `${this.props.fieldKey}.meta.${fieldName}`,
            format: value => ((value && moment(value).isValid()) ? moment(value).format('MM-DD-YYYY') : value),
            normalize: value => (moment(value, 'MM-DD-YYYY').isValid() ? moment(value).format('YYYY-MM-DD') : value),
            component: ({ input }) =>
                <React.Fragment>
                    <DayPickerInput
                        value={input.value}
                        onDayChange={(value, modifiers, dayPickerInput) => {
                            if (value) {
                                input.onChange(value);
                            } else {
                                const datePickerInputInstance = dayPickerInput.getInput();
                                input.onChange(datePickerInputInstance.value);
                            }
                        }}
                        placeholder='MM-DD-YYYY'
                        format='MM-DD-YYYY'
                        formatDate={(date, format) => (date ? moment(date).format(format) : '')}
                        parseDate={(str, format) => (moment(str, format).isValid() ? moment(str, format).toDate() : '')}
                        dayPickerProps={{ numberOfMonths: 1 }} />
                    {
                        Boolean(input.value && !moment(input.value).isValid())
                        && <ErrorLabel>Please enter a valid date.</ErrorLabel>
                    }
                </React.Fragment>,
        };

        return resultProps;
    }

    getBorderClassName() {
        return [
            get(this.props.workOrder, 'new') ? 'new' : false,
            this.state.fadingOut ? 'fadingOut' : false
        ].filter(item => item).join(' ');
    }

    handleDelete() {
        this.setState({
            fadingOut: true
        }, () => {
            this.fadeoutTimer = setTimeout(() => {
                this.fadeoutTimer = null;

                this.props.handleDelete();
            }, 400);
        });
    }

    handleClone() {
        this.props.handleClone();
    }

    openModal() {
        this.setState({ mountSearchModal: true });
    }

    closeModal = () => {
        setTimeout(() => this.setState({ mountSearchModal: false }), 10);
    }

    componentWillUnmount() {
        if (this.fadeoutTimer) {
            clearTimeout(this.fadeoutTimer);
        }
    }

    render() {
        const dateHelpText = (<Popover id="layer-aliases" placement="right" title="Effective Start and End Dates">
            These are currently not implemented, but it's good to enter the dates if you know them for future use.
            These dates will restrict the data to only be queried for a specific range.
            Helpful if you need to cutoff early test data in a campaign.
            If the campaign does not have an end date, just leave it blank.</Popover>);

        const { dispatch, fieldKey, formName, workOrder = {}, handleOverrideRemoval,
            switchWorkOrderPlatforms, defaultExpandChannels } = this.props;
        const { adminPlatform } = workOrder;
        return (
            <Fragment>
                <Row>
                    <Col xs={12}>
                        <ListItemTile className={this.getBorderClassName()}>
                            <UpperWorkOrder>
                                <Row>
                                    <Col xs={6}>
                                        <strong>
                                            <RequiredLabel required>
                                                {this.getAdminPlatformFromMap(ADMIN_PLATFORM_MAP)}
                                            </RequiredLabel>
                                        </strong>
                                    </Col>
                                    <Col xs={4}><strong>Admin Platform</strong></Col>
                                    <ActionsWrapper>
                                        <TrackedActionButton title="Copy Work Order" onClick={this.handleClone}><IconWrapper className="icon-clone" iconColor="#007DC6" width='20px' height='20px' fontSize="20px" marginRight='3px' /><span>Copy</span></TrackedActionButton>
                                        <ActionButton title="Delete Work Order" onClick={this.handleDelete}><IconWrapper className="icon-trash" iconColor="red" width='20px' height='20px' fontSize="20px" marginRight='3px' /><span>Delete</span></ActionButton>
                                    </ActionsWrapper>
                                </Row>
                                <Row>
                                    <Col xs={6}>
                                        <Field
                                            name={`${this.props.fieldKey}.${this.getAdminPlatformFromMap(ADMIN_PLATFORM_FIELD_MAP)}`}
                                            component={TextInputAdmin}
                                            required={true}
                                            placeholder={this.getAdminPlatformFromMap(ADMIN_PLATFORM_MAP)} />
                                        {
                                            (this.getAdminPlatformFromMap(ADMIN_PLATFORM_FIELD_MAP)
                                                === ADMIN_PLATFORM_FIELD_MAP.salesforce)
                                            && <LinkText
                                                onClick={() => this.openModal()}>
                                                <IconWrapper
                                                    iconColor="#337AB7"
                                                    className="icon-search"
                                                    fontSize="12px"
                                                    marginRight="0px"
                                                    verticalAlign="baseline"
                                                ></ IconWrapper>Find Opportunity</LinkText>
                                        }
                                    </Col>
                                    <Col xs={5}>{this.getAdminPlatformFromMap(ADMIN_PLATFORM_TITLE_MAP)}</Col>
                                </Row>
                                <Row>
                                    <Col xs={5}><strong>Description</strong></Col>
                                </Row>
                                <Row>
                                    <Col xs={6}><Field name={`${this.props.fieldKey}.meta.description`} required={false} component={TextInputAdmin} /></Col>
                                </Row>
                                <Row>
                                    <Col xs={6}>
                                        <FlexWrapper>
                                            <strong>Effective Start Date</strong>
                                            <OverlayTrigger trigger="click" placement="right" overlay={dateHelpText}>
                                                <HelpIcon />
                                            </OverlayTrigger>
                                        </FlexWrapper>
                                    </Col>
                                    <Col xs={5}>
                                        <FlexWrapper>
                                            <strong>Effective End Date</strong>
                                            <OverlayTrigger trigger="click" placement="right" overlay={dateHelpText}>
                                                <HelpIcon />
                                            </OverlayTrigger>
                                        </FlexWrapper>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={6}><Field {...this.createDatePickerProps('startDate')} /></Col>
                                    <Col xs={5}><Field {...this.createDatePickerProps('endDate')} /></Col>
                                </Row>
                            </UpperWorkOrder>
                            <WorkOrderChannelConfig
                                fieldKey={fieldKey}
                                adminPlatform={adminPlatform}
                                handleOverrideRemoval={handleOverrideRemoval}
                                switchWorkOrderPlatforms={switchWorkOrderPlatforms}
                                defaultExpand={defaultExpandChannels} />
                        </ListItemTile>
                    </Col>
                </Row>
                {this.state.mountSearchModal ?
                    <SearchOpportunities
                        dismiss={this.closeModal}
                        proceed={(id) => {
                            dispatch(
                                change(
                                    formName,
                                    `${fieldKey}.${this.getAdminPlatformFromMap(ADMIN_PLATFORM_FIELD_MAP)}`,
                                    id)
                            );
                            this.closeModal();
                        }
                        }
                    />
                    : null}
            </Fragment>
        );
    }
}

WorkOrder.propTypes = {
    fieldKey: PropTypes.string,
    workOrder: PropTypes.object,
    handleOverrideRemoval: PropTypes.func,
    switchWorkOrderPlatforms: PropTypes.func,
    searchForOpportunities: PropTypes.func
};

export default WorkOrder;



// WEBPACK FOOTER //
// ./src/components/organisms/entity/edit/WorkOrder.js